// REACT AND REACT HOOKS
import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';

// BOOTSTRAP COMPONENTS
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';

// DATATABLES LIBRARY (not used directly here, but imported for consistency)
import DataTable from 'datatables.net-react';

// UTILITY FUNCTIONS for extracting plain item names
import { getPlainName } from '../FP_foodPantryUtils';

// TABLE COLUMN CONFIGURATIONS (not used directly here, but available for future table displays)
import { columnsInventory, columnsTransactions } from '../FP_foodPantryTables';

// DONOR SEARCH MODAL
import { DonorSearchModal } from './FP_DonorSearchModal';

import introJs from 'intro.js';


// Modal for receiving new inventory items (scan/manual entry)
export function ReceiveItemsModal({
  show, // Boolean to show/hide modal
  onHide, // Function to close modal
  selectedOrgID, // Organization ID for API calls
  setInventory, // Setter for inventory state
  setShowTransactionReceiveModal, // Setter for receive modal visibility
  setShowTransactionReleaseModal, // Setter for release modal visibility
  setShowTransactionOtherModal, // Setter for other transactions modal visibility
  showReceiveItemSuccessBanner, // Function to show success notification
  foodPantryTourStager, // String to control tour staging
  setFoodPantryTourStager // Setter for tour staging
}) {
  // State for manual entry form visibility
  const [showManualForm, setShowManualForm] = useState(false);
  // List of items added to the preview before finalizing
  const [addedItems, setAddedItems] = useState([]);
  // All items and variations fetched from backend for suggestions and validation
  const [allItems, setAllItems] = useState([]);
  const [allVariations, setAllVariations] = useState([]);
  // Suggestions for autocomplete dropdowns
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [variationSuggestions, setVariationSuggestions] = useState([]);
  // Form input states
  const [itemNameInput, setItemNameInput] = useState("");
  const [variationNameInput, setVariationNameInput] = useState("");
  const [quantityInput, setQuantityInput] = useState(1);
  const [upcInput, setUpcInput] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [transactionType, setTransactionType] = useState("PURCHASE");

  // Unified code input and scan/manual mode
  const [codeInput, setCodeInput] = useState("");
  const [scanCode, setScanCode] = useState("");
  const codeInputRef = useRef(null);

  // Donor selection state
  const [donors, setDonors] = useState([]);
  const [selectedDonorID, setSelectedDonorID] = useState("");
  const [selectedDonorName, setSelectedDonorName] = useState("");
  const [loadingDonors, setLoadingDonors] = useState(false);
  const [showDonorSearch, setShowDonorSearch] = useState(false);

  // Fetch all items and variations when modal opens
  useEffect(() => {
    if (show) {
      const url = process.env.REACT_APP_BACKURL;
      // Fetch all items for suggestions and validation
      fetch(url + '/api/foodPantry/inventory/' + selectedOrgID + '/items', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        method: 'GET'
      })
        .then(res => res.json())
        .then(data => setAllItems(Array.isArray(data) ? data : []))
        .catch(() => setAllItems([]));

      // Fetch all variations for suggestions and validation
      fetch(url + '/api/foodPantry/inventory/' + selectedOrgID + '/all-variations', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        method: 'GET'
      })
        .then(res => res.json())
        .then(data => setAllVariations(Array.isArray(data) ? data : []))
        .catch(() => setAllVariations([]));

      // Fetch all donors from database
      setLoadingDonors(true);
      fetch(url + '/api/foodPantry/donations/' + selectedOrgID + '/donors', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        method: 'GET'
      })
        .then(res => res.json())
        .then(data => setDonors(Array.isArray(data) ? data : []))
        .catch(err => {
          console.error('Error fetching donors:', err);
          setDonors([]);
        })
        .finally(() => setLoadingDonors(false));
    }
    // Reset all form and preview state when modal closes
    if (!show) {
      setAddedItems([]);
      setItemNameInput("");
      setVariationNameInput("");
      setQuantityInput(1);
      setCodeInput("");
      setScanCode("");
      setEditIndex(null);
      setSelectedDonorID("");
      setSelectedDonorName("");
    }
  }, [show, selectedOrgID]);

  const itemNameInputRef = useRef(null);
  // Focus code input when scan mode is active
  useEffect(() => {
    if (!showManualForm && show && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [showManualForm, show]);


  useEffect(() => {
    if (showManualForm && show && itemNameInputRef.current) {
      itemNameInputRef.current.focus();
    }
  }, [showManualForm, show]);


  // Suggest items as user types in manual entry
  const handleItemNameChange = (e) => {
    const value = e.target.value;
    setItemNameInput(value);
    if (value.length > 0) {
      setItemSuggestions(
        allItems.filter(item =>
          item.name.toLowerCase().includes(value.toLowerCase())
        ).slice(0, 5)
      );
    } else {
      setItemSuggestions([]);
    }
    setVariationNameInput("");
    setVariationSuggestions([]);
  };

  // Suggest variations as user types in manual entry
  const handleVariationNameChange = (e) => {
    const value = e.target.value;
    setVariationNameInput(value);

    const selectedItem = allItems.find(
      item => item.name.toLowerCase() === itemNameInput.toLowerCase()
    );
    let filteredVariations = allVariations;
    if (selectedItem) {
      filteredVariations = allVariations.filter(
        variation => variation.itemID === selectedItem.itemID
      );
    }
    if (value.length > 0) {
      setVariationSuggestions(
        filteredVariations.filter(variation =>
          variation.name.toLowerCase().includes(value.toLowerCase())
        ).slice(0, 5)
      );
    } else {
      setVariationSuggestions([]);
    }
  };

  // Add item to preview list (manual or scan)
  const addItemToPreview = ({ item, variation, quantity, upc, unitValue }) => {
    if (!item || !variation) {
      showReceiveItemSuccessBanner(
        "Missing Data",
        <span>Item or variation does not exist. Please use the Create modal first.</span>,
        "alert"
      );
      return;
    }
    // Use variation.suggestedPrice as default
    const price = unitValue !== undefined
      ? unitValue
      : variation.suggestedPrice !== undefined
        ? variation.suggestedPrice
        : 0;
    if (editIndex !== null) {
      const updated = [...addedItems];
      updated[editIndex] = {
        itemName: item.name,
        variationName: variation.name,
        quantity,
        unitQty: item.unitQty,
        packSize: item.packSize,
        itemID: item.itemID,
        itemVariationID: variation.itemVariationID,
        upc: upc || variation.upc || "",
        unitValue: price,
      };
      setAddedItems(updated);
      setEditIndex(null);
    } else {
      const existingIdx = addedItems.findIndex(
        i =>
          i.itemID === item.itemID &&
          i.itemVariationID === variation.itemVariationID &&
          i.variationName === variation.name
      );
      if (existingIdx !== -1) {
        const updated = [...addedItems];
        updated[existingIdx].quantity =
          parseInt(updated[existingIdx].quantity, 10) + parseInt(quantity, 10);
        setAddedItems(updated);
      } else {
        setAddedItems(prev => [
          ...prev,
          {
            itemName: item.name,
            variationName: variation.name,
            quantity,
            unitQty: item.unitQty,
            packSize: item.packSize,
            itemID: item.itemID,
            itemVariationID: variation.itemVariationID,
            upc: upc || variation.upc || "",
            unitValue: price,
          }
        ]);
      }
    }
    // Reset form fields after adding
    setItemNameInput("");
    setVariationNameInput("");
    setQuantityInput(1);
    setUpcInput("");
    // ...existing code...
  };

  // Scan input logic: only submit on Enter, limit to 12 digits
  const handleScan = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 12);
    setScanCode(value);
  };

  const handleScanKeyDown = (e) => {
    if (e.key === "Enter" && scanCode.length > 0) {
      handleCodeScan(scanCode);
      setScanCode("");
      e.preventDefault();
    }
  };

  // Unified code scan handler: just search for any matching code
  const handleCodeScan = (value) => {
    let variation = allVariations.find(v => v.upc === value || v.plu === value || v.otherCode === value);
    if (!variation) {
      showReceiveItemSuccessBanner(
        "Code Not Found",
        <span>Code not found. Please check or create the variation first.</span>,
        "extreme-error"
      );
      return;
    }
    const item = allItems.find(i => i.itemID === variation.itemID);
    if (!item) {
      showReceiveItemSuccessBanner(
        "Item Not Found",
        <span>No item found for this variation.</span>,
        "extreme-error"
      );
      return;
    }
    addItemToPreview({ item, variation, quantity: 1, upc: value });
  };

  // Remove item from preview list
  const removeItem = (idx) => {
    setAddedItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Edit item in preview list
  const editItem = (idx) => {
    const item = addedItems[idx];
    setItemNameInput(item.itemName);
    setVariationNameInput(item.variationName);
    setQuantityInput(item.quantity);
    setUpcInput(item.upc);
    setEditIndex(idx);
    setShowManualForm(true);
    // Optionally set unitValue for editing
  };

  // Manual form submit handler
  const handleManualSubmit = (e) => {
    e.preventDefault();
    const codeToUse = codeInput.replace(/\D/g, "");
    let item = null;
    let variation = null;
    let codeField = codeToUse;
    if (codeToUse) {
      variation = allVariations.find(v => v.upc === codeToUse || v.plu === codeToUse || v.otherCode === codeToUse);
      if (!variation) {
        showReceiveItemSuccessBanner(
          "Code Not Found",
          <span>Code not found. Please use the Create modal first.</span>,
          "extreme-error"
        );
        return;
      }
      item = allItems.find(i => i.itemID === variation.itemID);
      if (!item) {
        showReceiveItemSuccessBanner(
          "Item Not Found",
          <span>No item found for this variation.</span>,
          "extreme-error"
        );
        return;
      }
      setItemNameInput(item.name);
      setVariationNameInput(variation.name);
    } else if (itemNameInput && variationNameInput) {
      item = allItems.find(i => i.name === itemNameInput);
      if (!item) {
        showReceiveItemSuccessBanner(
          "Item Not Found",
          <span>Item does not exist. Please use the Create modal first.</span>,
          "alert"
        );
        return;
      }
      variation = allVariations.find(
        v => v.name === variationNameInput && v.itemID === item.itemID
      );
      if (!variation) {
        showReceiveItemSuccessBanner(
          "Variation Not Found",
          <span>Variation does not exist. Please use the Create modal first.</span>,
          "alert"
        );
        return;
      }
      // Auto-fill code based on available code type
      if (!codeToUse) {
        if (variation.upc) {
          codeField = variation.upc;
          setCodeInput(variation.upc);
        } else if (variation.plu) {
          codeField = variation.plu;
          setCodeInput(variation.plu);
        } else if (variation.otherCode) {
          codeField = variation.otherCode;
          setCodeInput(variation.otherCode);
        } else {
          showReceiveItemSuccessBanner(
            "No Code Available",
            <span>No available code tied to this variation.</span>,
            "alert"
          );
          return;
        }
      }
    } else {
      showReceiveItemSuccessBanner(
        "Missing Info",
        <span>Please provide either a valid code or both Item Name and Variation Name.</span>,
        "alert"
      );
      return;
    }
    const qty = parseInt(quantityInput, 10);
    if (!qty || qty < 1) {
      showReceiveItemSuccessBanner(
        "Invalid Quantity",
        <span>Enter a valid quantity.</span>,
        "alert"
      );
      return;
    }
    addItemToPreview({ item, variation, quantity: qty, upc: codeField });
    setCodeInput("");
  };

  // Handle donor selection from search modal
  const handleDonorSelected = (selectedDonor) => {
    setSelectedDonorID(selectedDonor.donorID);
    if (selectedDonor.organizationName) {
      setSelectedDonorName(selectedDonor.organizationName);
    } else if (selectedDonor.clientFirstName) {
      setSelectedDonorName(`${selectedDonor.clientFirstName} ${selectedDonor.clientLastName || ""}`.trim());
    } else if (selectedDonor.volunteerFirstName) {
      setSelectedDonorName(`${selectedDonor.volunteerFirstName} ${selectedDonor.volunteerLastName || ""}`.trim());
    } else {
      setSelectedDonorName(selectedDonor.donorDisplayName || `Donor ${selectedDonor.donorID}`);
    }
  };

  // Finalize: POST all receives to backend and update inventory
  const finalizeReceive = async () => {
    const url = process.env.REACT_APP_BACKURL;
    try {
      // If donation type, create donation record first
      let donationID = null;
      const inventoryIDs = [];

      if (transactionType === "DONATION") {
        if (!selectedDonorID) {
          throw new Error("Please select a donor for this donation");
        }
        // Create donation record
        const donationRes = await fetch(
          url + `/api/foodPantry/donations/${selectedOrgID}`,
          {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
            body: JSON.stringify({
              donorID: selectedDonorID,
              donationDate: new Date().toISOString().split('T')[0],
              inventoryIDs: []
            })
          }
        );
        const donationResult = await donationRes.json();
        if (!donationRes.ok) {
          throw new Error(donationResult.message || "Failed to create donation record");
        }
        donationID = donationResult.donationID;
      }

      // Post all inventory items
      for (const item of addedItems) {
        const res = await fetch(
          url + `/api/foodPantry/inventory/${selectedOrgID}/intake-inventory-item`,
          {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
            body: JSON.stringify({
              inventory: {
                itemVariationID: item.itemVariationID,
                quantity: parseInt(item.quantity, 10),
                expirationDate: new Date().toISOString().split('T')[0],
                transactionDate: new Date().toISOString().split('T')[0],
                orgID: selectedOrgID,
                unitValue: item.unitValue,
                transactionType: transactionType,
                donationID: donationID // Link to donation if applicable
              }
            })
          }
        );
        const result = await res.json();
        if (!res.ok) {
          throw new Error(result.message || "Failed to receive item");
        }
        if (result.inventoryID) {
          inventoryIDs.push(result.inventoryID);
        }
      }

      // If donation was created, update it with inventory IDs
      if (donationID && inventoryIDs.length > 0) {
        await fetch(
          url + `/api/foodPantry/donations/${selectedOrgID}`,
          {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
            body: JSON.stringify({
              donorID: selectedDonorID,
              donationDate: new Date().toISOString().split('T')[0],
              inventoryIDs: inventoryIDs
            })
          }
        ).catch(err => console.warn('Warning: Could not update donation with inventory IDs:', err));
      }

      // Add received items to inventory state
      setInventory(prev => [
        ...prev,
        ...addedItems.map(item => {
          const found = allItems.find(i => i.itemID === item.itemID);
          return {
            ...item,
            category: found?.category || "",
            qoh: parseInt(item.quantity, 10) || 0, // Ensure qoh is present
          };
        })
      ]);
      // Reset preview and close modals
      setAddedItems([]);
      setSelectedDonorID("");
      setShowTransactionReceiveModal(false);
      setShowTransactionReleaseModal(false);
      setShowTransactionOtherModal(false);

      showReceiveItemSuccessBanner(
        "Success",
        (() => {
          const maxNames = 7;
          const names = addedItems.map(i => i.variationName);
          const count = addedItems.length;
          const plural = count === 1 ? "Item" : "Items";
          const donationMsg = transactionType === "DONATION" ? " - Donation recorded" : "";
          if (names.length > maxNames) {
            return (
              <span>
                <strong>{count} {plural} received{donationMsg}:</strong> {names.slice(0, maxNames).join(", ")}, and {names.length - maxNames} others.
              </span>
            );
          }
          return (
            <span>
              <strong>{count} {plural} received{donationMsg}:</strong> {names.join(", ")}
            </span>
          );
        })(),
        "success"
      );

    } catch (err) {
      showReceiveItemSuccessBanner(
        "Critical Error",
        <span>Failed to finalize receiving: {err.message}</span>,
        "extreme-error"
      );
    }
  };

  // --- Intro.js Tour Setup ---
  const receiveItemsTourSteps = [
    {
      element: '[data-step="4"]',
      intro: "Here is where you can enter an item into inventory based off of its UPC code if its allready in the system. It also accepts PLU or Alt-ID",
      hidePrev: true
    },
    {
      element: '[data-step="5"]',
      intro: "An item can also be added in manually by clicking this button.",
      hidePrev: true
    },
    {
      element: '[data-step="6"]',
      intro: "Here simply enter the information about the item, either its name and variation or just the UPC/PLU/Alt-ID or all of it. Once you are done click the add button",
      hidePrev: true
    },
    {
      element: '[data-step="7"]',
      intro: "Once you add an item, it will appear in this preview table. Let's see what it would look like.",
      hidePrev: true
    },
    {
      element: '[data-step="row-highlight"]',
      intro: "Before Finalizing the inventory you are about to add you can edit individual item's quantity receiving and the price per quantity.",
      hidePrev: true
    },
    {
      element: '[data-step="transaction-type"]',
      intro: "Before Finalizing be sure to select a transaction type from the dropdown list.",
      hidePrev: true
    },
    {
      element: '[data-step="finalize-receive"]',
      intro: "Click here to finalize receiving the items into inventory.",
      hidePrev: true
    }
  ];


  const [phase2Start, setPhase2Start] = useState(false);
  const [phase3Start, setPhase3Start] = useState(false);

  useEffect(() => {
    if (show && foodPantryTourStager === "Receive") {
      // Wait until previous tour is fully cleaned up (no .introjs-overlay) AND at least 400ms have passed
      const minDelay = 400;
      const startTime = Date.now();
      const startTourWhenReady = () => {
        const elapsed = Date.now() - startTime;
        if (document.querySelector('.introjs-overlay') || elapsed < minDelay) {
          setTimeout(startTourWhenReady, 50);
          return;
        }
        setShowManualForm(false);
        let manualStepCounter = 0;
        const intro = introJs();
        intro.setOptions({
          steps: receiveItemsTourSteps,
          showBullets: false,
          exitOnOverlayClick: false
        });
        intro.onchange(function () {
          manualStepCounter++;
          if (manualStepCounter === 3) {
            intro.exit();
            setShowManualForm(true);
            setPhase2Start(true);
          }
        });
        intro.start();
        // Hide the back button and force next/done to say 'Next'
        intro.onbeforechange(function () {
          setTimeout(() => {
            const prevBtn = document.querySelector('.introjs-prevbutton');
            if (prevBtn) prevBtn.style.display = 'none';
            const nextBtn = document.querySelector('.introjs-nextbutton');
            if (nextBtn) nextBtn.textContent = 'Next';
            const doneBtn = document.querySelector('.introjs-donebutton');
            if (doneBtn) doneBtn.textContent = 'Next';
          }, 0);
        });
        intro.onComplete(() => {
          setShowManualForm(true);
          setPhase2Start(true);
        });
      };
      startTourWhenReady();
    }
  }, [show, foodPantryTourStager]);


  useEffect(() => {
    if (phase2Start) {
      setPhase2Start(false);
      const intro = introJs();
      intro.setOptions({
        steps: receiveItemsTourSteps,
        showBullets: false,
        exitOnOverlayClick: false
      });
      let manualStepCounter = 2; // Start at step 3 (index 2)
      setTimeout(() => {
        intro.start();
        setTimeout(() => {
          intro.goToStep(3); // Start at the third step (index 2)
        }, 100);
        // Hide the back button and force next/done to say 'Next'
        intro.onbeforechange(function () {
          setTimeout(() => {
            const prevBtn = document.querySelector('.introjs-prevbutton');
            if (prevBtn) prevBtn.style.display = 'none';
            const nextBtn = document.querySelector('.introjs-nextbutton');
            if (nextBtn) nextBtn.textContent = 'Next';
            const doneBtn = document.querySelector('.introjs-donebutton');
            if (doneBtn) doneBtn.textContent = 'Next';
          }, 0);
        });
      }, 500);
      intro.onchange(function () {
        manualStepCounter++;
        // Step 4 is the preview table, step 5 is the trashcan (data-step 8)
        // We want to stop after preview table (step 4, index 3)
        if (manualStepCounter === 6) {
          intro.exit();
          // Simulate adding a fully populated example item to the preview list
          setAddedItems(prev => [
            ...prev,
            {
              itemID: "example-item-123",
              itemName: "Sample Beans",
              variationName: "Canned - 16oz",
              quantity: 5,
              unitQty: "16oz",
              packSize: "12-pack",
              itemVariationID: "example-var-456",
              upc: "123456789012",
              unitValue: 2.49
            }
          ]);
          setPhase3Start(true);
        }
      });
      intro.onComplete(() => {
        // Simulate adding a fully populated example item to the preview list
        setAddedItems(prev => [
          ...prev,
          {
            itemID: "example-item-123",
            itemName: "Sample Beans",
            variationName: "Canned - 16oz",
            quantity: 5,
            unitQty: "16oz",
            packSize: "12-pack",
            itemVariationID: "example-var-456",
            upc: "123456789012",
            unitValue: 2.49
          }
        ]);
        setPhase3Start(true);
      });
    }
  }, [phase2Start]);

  useEffect(() => {
    if (phase3Start) {
      setPhase3Start(false);
      const Intro = introJs();
      Intro.setOptions({
        steps: receiveItemsTourSteps,
        showBullets: false,
        exitOnOverlayClick: false
      });
      setTimeout(() => {
        Intro.start();
        setTimeout(() => {
          Intro.goToStep(5); // Start at the fifth step (index 4)
        }, 100);
        // Hide the back button and force next/done to say 'Next'
        Intro.onbeforechange(function () {
          setTimeout(() => {
            const prevBtn = document.querySelector('.introjs-prevbutton');
            if (prevBtn) prevBtn.style.display = 'none';
            const nextBtn = document.querySelector('.introjs-nextbutton');
            if (nextBtn) nextBtn.textContent = 'Next';
            const doneBtn = document.querySelector('.introjs-donebutton');
            if (doneBtn) doneBtn.textContent = 'Next';
          }, 0);
        });
      }, 500);
      Intro.onComplete(() => {
        setFoodPantryTourStager("Release");
        //clear preview list and reset modal state
        setAddedItems([]);
        setItemNameInput("");
        setVariationNameInput("");
        setQuantityInput(1);
        setCodeInput("");
        setScanCode("");
        setEditIndex(null);
        setShowManualForm(false);
        onHide();
      });
    }
  }, [phase3Start]);





  // --- Render modal UI ---
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      aria-labelledby="receive-items-modal"
      centered
      dialogClassName="min-size-modal"
    >
      <div className="text-dark">
        {/* Modal header with title and close button */}
        <Modal.Header>
          <div className="d-flex justify-content-center w-100 position-relative">
            <Modal.Title id="receive-items-modal">Receive Items</Modal.Title>
            <Button
              variant="light"
              onClick={onHide}
              className="position-absolute top-0 end-0"
            >
              <i className="fa fa-times"></i>
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-4">
            {/* Left column: scan/manual entry form */}
            <div className="col-md-6">
              <div className="d-flex mb-3 align-items-center justify-content-between w-100">
                {/* Toggle between scan and manual entry */}
                <div data-step="5">
                  <Button
                    variant="primary"
                    onClick={() => setShowManualForm(prev => !prev)}
                  >
                    {showManualForm ? "Back to Scan" : "Add Manually"}
                  </Button>
                </div>
                {/* ...existing code... (triple selector removed) */}
              </div>
              {/* Manual entry form for adding items */}
              {showManualForm && (
                <form onSubmit={handleManualSubmit}
                  data-step="6"
                >
                  <div className="row g-2">
                    {/* <div className="col-md-6 position-relative form-floating">
                      <input
                        type="text"
                        placeholder="Item Name"
                        className="my-1 form-control"
                        name="itemName"
                        value={itemNameInput}
                        onChange={handleItemNameChange}
                        autoComplete="off"
                        required={!codeInput}
                        ref={itemNameInputRef}
                      />
                      <label htmlFor="text" className="form-label">
                        Item Name
                      </label>

                      {/* Item suggestions dropdown
                      {itemSuggestions.length > 0 && (
                        <ul className="list-group position-absolute" style={{ zIndex: 10 }}>
                          {itemSuggestions.map((item, idx) => (
                            <li
                              key={item.itemID}
                              className="list-group-item list-group-item-action"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setItemNameInput(item.name);
                                setItemSuggestions([]);
                              }}
                            >
                              {item.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div> */}

                    <div className="col-md-6 position-relative form-floating">
                      <Select
                        classNamePrefix="react-select"
                        className="form-floating"
                        placeholder="Item Name"
                        name="itemName"
                        value={
                          itemNameInput
                            ? { value: itemNameInput, label: itemNameInput }
                            : null
                        }
                        onChange={option => {
                          setItemNameInput(option ? option.value : "");
                          setVariationNameInput("");
                          setVariationSuggestions([]);
                        }}
                        options={allItems.map(item => ({
                          value: item.name,
                          label: item.name
                        }))}
                        isSearchable
                        isClearable
                        required={!codeInput}
                        ref={itemNameInputRef}
                        styles={{
                          valueContainer: (provided) => ({
                            ...provided,
                            paddingLeft: 0 // Remove left padding
                          })
                        }}
                      />
                      {/* <label htmlFor="itemName" className="form-label">
                        Item Name
                      </label> */}
                    </div>


                    <div className="col-md-6 position-relative form-floating">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          !itemNameInput ? (
                            <Tooltip id="variation-tooltip">
                              Select an item first
                            </Tooltip>
                          ) : <></>
                        }
                      >
                        <div>
                          <Select
                            classNamePrefix="react-select"
                            className="form-floating"
                            placeholder="Variation Name"
                            name="variationName"
                            value={
                              variationNameInput
                                ? { value: variationNameInput, label: variationNameInput }
                                : null
                            }
                            onChange={option => {
                              setVariationNameInput(option ? option.value : "");
                              setVariationSuggestions([]);
                              // Autofill code if available
                              if (option) {
                                const selectedItem = allItems.find(item => item.name === itemNameInput);
                                const selectedVariation = allVariations.find(
                                  v => v.name === option.value && selectedItem && v.itemID === selectedItem.itemID
                                );
                                if (selectedVariation) {
                                  if (selectedVariation.upc && selectedVariation.upc.trim() !== "") {
                                    setCodeInput(selectedVariation.upc);
                                  } else if (selectedVariation.plu && selectedVariation.plu.trim() !== "") {
                                    setCodeInput(selectedVariation.plu);
                                  } else if (selectedVariation.otherCode && selectedVariation.otherCode.trim() !== "") {
                                    setCodeInput(selectedVariation.otherCode);
                                  }
                                }
                              }
                            }}
                            options={
                              (() => {
                                const selectedItem = allItems.find(item => item.name === itemNameInput);
                                let filteredVariations = allVariations;
                                if (selectedItem) {
                                  filteredVariations = allVariations.filter(
                                    variation => variation.itemID === selectedItem.itemID
                                  );
                                }
                                return filteredVariations.map(variation => ({
                                  value: variation.name,
                                  label: variation.name
                                }));
                              })()
                            }
                            isSearchable
                            isClearable
                            required={!codeInput}
                            isDisabled={!itemNameInput}
                            styles={{
                              valueContainer: (provided) => ({
                                ...provided,
                                paddingLeft: 0 // Remove left padding
                              })
                            }}
                          />
                          {/* <label htmlFor="variationName" className="form-label">
                            Variation Name
                          </label> */}
                        </div>
                      </OverlayTrigger>
                    </div>
                  </div>

                  {/*  <div className="col-md-6 position-relative form-floating">
                      <input
                        type="text"
                        placeholder="Variation Name"
                        className="my-1 form-control"
                        name="variationName"
                        value={variationNameInput}
                        onChange={handleVariationNameChange}
                        autoComplete="off"
                        required={!codeInput}
                        disabled={!itemNameInput}
                      />
                      <label htmlFor="text" className="form-label">
                        Variation Name
                      </label>

                      {/* Variation suggestions dropdown
                      {variationSuggestions.length > 0 && (
                        <ul className="list-group position-absolute" style={{ zIndex: 10 }}>
                          {variationSuggestions.map((variation, idx) => (
                            <li
                              key={variation.itemVariationID}
                              className="list-group-item list-group-item-action"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setVariationNameInput(variation.name);
                                setVariationSuggestions([]);
                                if (variation.upc && variation.upc.trim() !== "") {
                                  setCodeInput(variation.upc);
                                } else if (variation.plu && variation.plu.trim() !== "") {
                                  setCodeInput(variation.plu);
                                } else if (variation.otherCode && variation.otherCode.trim() !== "") {
                                  setCodeInput(variation.otherCode);
                                }
                              }}
                            >
                              {variation.name}
                            </li>
                          ))}
                        </ul>
                      )} */}
                  <div className="row g-2">
                    <div className="col-md-6 form-floating">
                      <input
                        type="number"
                        placeholder="Receive Quantity"
                        className="form-control my-1"
                        name="quantity"
                        value={quantityInput}
                        onChange={e => {
                          let val = e.target.value.replace(/\D/g, "");
                          if (val.length > 4) val = val.slice(0, 4);
                          setQuantityInput(val);
                        }}
                        required
                        min={1}
                        max={9999}
                      />
                      <label htmlFor="text" className="form-label">
                        Receive Quantity
                      </label>

                    </div>
                    <div className="col-md-6 form-floating">
                      <input
                        type="text"
                        className="form-control my-1"
                        name="code"
                        value={codeInput}
                        onChange={e => setCodeInput(e.target.value.replace(/\D/g, ""))}
                        maxLength={12}
                        inputMode="numeric"
                        required={!(itemNameInput && variationNameInput)}
                        placeholder="Enter UPC/PLU/Other code"
                        pattern="\d{4,12}"
                        ref={codeInputRef}
                      />
                      <label htmlFor="text" className="form-label">
                        UPC/PLU/Other Code
                      </label>

                    </div>
                  </div>
                  <div className="d-flex justify-content-end my-3">
                    <Button type="submit" variant="success">
                      {editIndex !== null ? "Update" : "Add"}
                    </Button>
                  </div>
                </form>
              )}
              {/* Scan mode for quick code entry */}
              {!showManualForm && (
                <>
                  <div className="d-flex align-items-center justify-content-center py-5 gap-3">
                    <i className="bi bi-upc-scan fs-2"></i>
                    <h4 className="mb-0">Scan or enter code...</h4>
                  </div>
                  <div className="d-flex align-items-center my-3 gap-2"
                    data-step="4"
                  >
                    <input
                      type="text"
                      inputMode="numeric"
                      className="form-control"
                      placeholder="Scan or enter UPC/PLU/Other code"
                      ref={codeInputRef}
                      value={scanCode}
                      onChange={handleScan}
                      onKeyDown={handleScanKeyDown}
                      maxLength={12}
                      style={{ flex: 1 }}
                    />
                  </div>
                </>
              )}
            </div>
            {/* Right column: preview list of items to be received */}
            <div className="col-md-6 pb-3 d-flex flex-column">
              <div className="card m-0 bg-light flex-grow-1 d-flex flex-column" data-step="7">
                {addedItems.length === 0 ? (
                  <div className="flex-grow-1 d-flex justify-content-center align-items-center text-muted">
                    No items added yet
                  </div>
                ) : (
                  <div className="table-responsive flex-grow-1">
                    <table className="table table-sm table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>Name/Variation</th>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Unit Value</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {addedItems.map((item, index) => (
                          <tr key={index} data-step={index === 0 ? "row-highlight" : undefined}>
                            <td>
                              <div style={{ fontWeight: 'bold' }}>{item.variationName}</div>
                              <div style={{ fontSize: 'smaller', color: '#666' }}>{item.itemName}</div>
                            </td>
                            <td>
                              <input
                                type="number"
                                min={1}
                                value={item.quantity === undefined || item.quantity === null ? "" : item.quantity}
                                onChange={e => {
                                  const val = e.target.value;
                                  const updated = [...addedItems];
                                  // Allow empty string, otherwise set as number
                                  updated[index].quantity = val === "" ? "" : parseInt(val, 10);
                                  setAddedItems(updated);
                                }}
                                style={{ width: 50, textAlign: "center" }}
                              />
                            </td>
                            <td>{item.unitQty}</td>
                            <td>
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                value={item.unitValue === undefined || item.unitValue === null ? "" : item.unitValue}
                                onChange={e => {
                                  const val = e.target.value;
                                  const updated = [...addedItems];
                                  // Allow empty string, otherwise set as number
                                  updated[index].unitValue = val === "" ? "" : parseFloat(val);
                                  setAddedItems(updated);
                                }}
                                style={{ width: 80, textAlign: "center" }}
                              />
                            </td>
                            <td className="text-center align-middle" style={{ verticalAlign: "middle" }}>
                              <Button
                                variant="danger"
                                size="sm"
                                className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                                style={{ width: 32, height: 32, padding: 0 }}
                                onClick={() => removeItem(index)}
                                title="Remove from list"
                              >
                                <i className="bi bi-trash" style={{ fontSize: "1.2rem", margin: "0 auto" }}></i>
                                <span className="visually-hidden">Remove</span>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {/* Finalize button to submit all received items */}
              {addedItems.length > 0 && (
                <div className="d-flex flex-column gap-3 mt-3">
                  <div className="d-flex flex-row flex-md-row gap-3 align-items-end">
                    <div className="form-group flex-grow-1 me-3"
                      data-step="transaction-type"
                    >
                      <label htmlFor="transactionType" className="form-label mb-2">
                        Transaction Type
                      </label>
                      <select
                        id="transactionType"
                        className="form-select"
                        value={transactionType}
                        onChange={e => setTransactionType(e.target.value)}
                        style={{ minHeight: "48px", fontSize: "1.1rem" }}
                        required
                      >
                        <option value="DONATION">Donation</option>
                        <option value="PURCHASE">Purchase</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    {/* Finalize button */}
                    <Button
                      variant="success"
                      onClick={finalizeReceive}
                      style={{
                        height: "48px",
                        fontSize: "1.1rem",
                        marginBottom: "0",
                        marginLeft: "16px"
                      }}
                      data-step="finalize-receive"
                    >
                      Finalize
                    </Button>
                  </div>
                  
                  {/* Donor selector - only show when transaction type is DONATION */}
                  {transactionType === "DONATION" && (
                    <div className="form-group">
                      <label className="form-label mb-2">
                        Select Donor <span style={{ color: 'red' }}>*</span>
                      </label>
                      <div className="d-flex gap-2 align-items-center">
                        <div
                          className="form-control bg-light"
                          style={{
                            minHeight: "48px",
                            fontSize: "1.1rem",
                            display: "flex",
                            alignItems: "center",
                            padding: "12px",
                            borderRadius: "4px"
                          }}
                        >
                          {selectedDonorName || "No donor selected"}
                        </div>
                        <Button
                          variant="primary"
                          onClick={() => setShowDonorSearch(true)}
                          style={{
                            minHeight: "48px",
                            fontSize: "1.1rem",
                            minWidth: "120px"
                          }}
                        >
                          <i className="bi bi-search me-2"></i>
                          Search
                        </Button>
                      </div>
                      {!selectedDonorID && transactionType === "DONATION" && (
                        <small className="text-danger d-block mt-2">Please select a donor before finalizing</small>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Donor Search Modal */}
              <DonorSearchModal
                show={showDonorSearch}
                onHide={() => setShowDonorSearch(false)}
                selectedOrgID={selectedOrgID}
                onSelectDonor={handleDonorSelected}
                donors={donors}
              />
            </div>
          </div>
        </Modal.Body>
      </div>
    </Modal>
  );
}
